/* eslint-disable react/no-unstable-nested-components */
import React, { Fragment } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { Box, CircularProgress, Grid, Skeleton, Table } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import * as R from 'ramda'
import { NumberUtils } from '@pbt/pbt-ui-components'

import { CollapsibleInformationList } from '~/components/elements/CollapsibleInformationList/CollapsibleInformationList'
import Typography from '~/components/elements/Typography/Typography'
import { useIsChewyCheckoutEnabled } from '~/store/hooks/business'
import { useGetInvoiceV3Items } from '~/store/hooks/invoiceV3'
import { getCurrentBusiness } from '~/store/reducers/auth'
import {
  getInvoiceV3,
  getInvoiceV3Loading,
  useIsAnyPrepaid,
} from '~/store/reducers/invoiceV3'

import InvoiceStatusLabel from '../../invoices/InvoiceStatusLabel'
import { LineItemComponentsActions } from '../../soapV2/collapsibleRail/summary/orders/orderSummaryUtils'
import SoapWidget from '../../soapV2/SoapWidget'
import ChargesPanel from '../components/ChargesPanel'
import InvoiceTableFooter from './InvoiceTableFooter'
import { InvoiceTableHeader } from './InvoiceTableHeader'
import InvoiceTableSubHeader from './InvoiceTableSubHeader'

const useStyles = makeStyles(
  (theme) => ({
    container: {
      margin: theme.spacing(0, 1, 1.5),
      width: 'unset',
    },
    chargeInformationPanel: {
      top: theme.mixins.toolbar.minHeight,
      maxHeight: `calc(100vh - ${theme.mixins.toolbar.minHeight}px)`,
    },
  }),
  { name: 'ViewInvoice' },
)

interface InvoiceDetailsProps {
  useInvoiceCVCLayout: boolean
}

export const InvoiceDetails = ({
  useInvoiceCVCLayout,
}: InvoiceDetailsProps) => {
  const classes = useStyles({ useInvoiceCVCLayout })
  const { invoiceId } = useParams()
  const { t } = useTranslation(['Common', 'Invoices'])

  const business = useSelector(getCurrentBusiness)
  const isInvoiceLoading = useSelector(getInvoiceV3Loading)
  const invoice = useSelector(getInvoiceV3(invoiceId))
  const {
    additionalDiscount = 0,
    refunds,
    subtotal,
    totalDiscount = 0,
    totalTax,
    amountNoFee,
  } = invoice || {}

  const hasRefunds = !R.isEmpty(refunds || [])

  const items = useGetInvoiceV3Items(invoiceId)
  const showPaidUsedRemainingLabel = useIsAnyPrepaid()
  const isChewyCheckoutEnabled = useIsChewyCheckoutEnabled()

  const InvoiceWrapper = useInvoiceCVCLayout ? SoapWidget : Fragment
  const InvoiceWrapperProps = useInvoiceCVCLayout
    ? {
        title: (
          <Box
            alignItems="center"
            display="flex"
            justifyContent="space-between"
            width="100%"
          >
            <Typography.H2 color="brandPrimary">
              {t('Common:CHEWY_VET_CARE')}
            </Typography.H2>
            {isInvoiceLoading ? (
              <Skeleton variant="text" width={32} />
            ) : (
              <InvoiceStatusLabel
                fontSize="1.2rem"
                invoice={invoice!}
                variant="default"
              />
            )}
          </Box>
        ),
        classes: {
          container: classes.container,
        },
      }
    : {}

  if (!isInvoiceLoading && R.isEmpty(items) && isChewyCheckoutEnabled) {
    return null
  }

  return (
    <InvoiceWrapper {...(InvoiceWrapperProps as any)}>
      <Table>
        <InvoiceTableHeader
          hasRefunds={hasRefunds}
          showPaidUsedRemainingLabel={showPaidUsedRemainingLabel}
          useInvoiceCVCLayout={useInvoiceCVCLayout}
        />
      </Table>
      <Grid container item>
        <Grid container item alignItems="center" justifyContent="center">
          {isInvoiceLoading ? (
            <CircularProgress color="primary" size={32} />
          ) : (
            <>
              <ChargesPanel
                isInvoice
                ComponentsActions={LineItemComponentsActions}
                Footer={(props) => (
                  <InvoiceTableFooter
                    {...props}
                    useInvoiceCVCLayout={useInvoiceCVCLayout}
                  />
                )}
                SubHeader={(props) => (
                  <InvoiceTableSubHeader
                    {...props}
                    colSpan={refunds?.length ? 7 : 6}
                    useInvoiceCVCLayout={useInvoiceCVCLayout}
                  />
                )}
                chargesGroupedItems={items}
                chargesId={invoiceId}
                classes={{
                  chargeInformationPanel: classes.chargeInformationPanel,
                }}
                hasHeader={false}
                invoiceId={invoiceId}
              />
              {useInvoiceCVCLayout && (
                <Box display="flex" px={2} py={1} width="100%">
                  {business?.invoiceNotes && (
                    <Typography.Paragraph color="grayGray2">
                      {business.invoiceNotes}
                    </Typography.Paragraph>
                  )}

                  <CollapsibleInformationList
                    initiallyOpened
                    // width to dynamically align with invoice footer
                    ContainerProps={{
                      ml: 'auto',
                      width: 'calc(23.75% - 6px)',
                    }}
                    list={[
                      {
                        id: 'subtotal',
                        label: t('Common:SUBTOTAL'),
                        value: NumberUtils.formatMoney(subtotal),
                      },
                      {
                        id: 'discount',
                        label: t('Common:DISCOUNT'),
                        value: NumberUtils.formatMoney(
                          -(totalDiscount + additionalDiscount),
                        ),
                      },
                      {
                        id: 'tax',
                        label: t('Common:TAX'),
                        value: NumberUtils.formatMoney(totalTax),
                      },
                    ]}
                    title={t('Common:INVOICE_TOTAL')}
                    titleValue={NumberUtils.formatMoney(amountNoFee)}
                  />
                </Box>
              )}
            </>
          )}
        </Grid>
      </Grid>
    </InvoiceWrapper>
  )
}
